import { Goal, GoalFormData, GoalList, SmartCriteria } from '../types/goals';

const GOALS_STORAGE_KEY = 'godmode_goals';

export class GoalsService {
  private goals: Goal[] = [];

  constructor() {
    this.loadGoals();
    // Ensure initial data has unique priorities
    this.normalizeAllPriorities();
  }

  // Load goals from localStorage
  private loadGoals(): void {
    try {
      const storedGoals = localStorage.getItem(GOALS_STORAGE_KEY);
      if (storedGoals) {
        const parsed = JSON.parse(storedGoals);
        // Convert date strings back to Date objects
        this.goals = parsed.map((goal: any) => ({
          ...goal,
          deadline: goal.deadline ? new Date(goal.deadline) : undefined,
          startDate: goal.startDate ? new Date(goal.startDate) : undefined,
          completedDate: goal.completedDate ? new Date(goal.completedDate) : undefined,
          createdAt: new Date(goal.createdAt),
          updatedAt: new Date(goal.updatedAt),
          lists: goal.lists.map((list: any) => ({
            ...list,
            deadline: new Date(list.deadline),
            completedAt: list.completedAt ? new Date(list.completedAt) : undefined,
          }))
        }));
      }
    } catch (error) {
      console.error('Error loading goals:', error);
      this.goals = [];
    }
  }

  // Save goals to localStorage
  private saveGoals(): void {
    try {
      localStorage.setItem(GOALS_STORAGE_KEY, JSON.stringify(this.goals));
    } catch (error) {
      console.error('Error saving goals:', error);
    }
  }

  // Initialize with mock data if empty
  initializeMockData(): void {
    // Data initialization is now handled by personaService
    // This method is kept for compatibility but doesn't generate mock data
    if (this.goals.length === 0) {
      console.log('📊 No goals found, data should be loaded from persona');
    }
  }

  // Get all goals
  getAllGoals(): Goal[] {
    return [...this.goals].sort((a, b) => a.priority - b.priority);
  }

  // Get goal by ID
  getGoalById(id: string): Goal | undefined {
    return this.goals.find(goal => goal.id === id);
  }

  // Get goals by aspiration ID
  getGoalsByAspirationId(aspirationId: string): Goal[] {
    return this.goals.filter(goal => goal.aspirationId === aspirationId)
      .sort((a, b) => a.priority - b.priority);
  }

  // Create new goal
  async createGoal(data: GoalFormData): Promise<Goal> {
    console.log('🆕 Creating new goal with aspiration-scoped priority:', data.priority);
    console.log('🎯 Target aspiration:', data.aspirationId);
    
    const targetPriority = data.priority;
    
    // Check if the target priority is already taken within the same aspiration
    const existingAtPriority = this.goals.find(g => 
      g.priority === targetPriority && g.aspirationId === data.aspirationId
    );
    
    if (existingAtPriority) {
      console.log(`⚠️ Priority ${targetPriority} already exists in aspiration ${data.aspirationId}, adjusting priorities`);
      // Shift priorities within the aspiration
      this.goals.forEach(goal => {
        if (goal.aspirationId === data.aspirationId && goal.priority >= targetPriority) {
          goal.priority += 1;
          goal.updatedAt = new Date();
        }
      });
    }

    const newGoal: Goal = {
      id: this.generateId(),
      ...data,
      priority: targetPriority,
      status: 'not_started',
      progress: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      lists: [],
    };
    
    this.goals.push(newGoal);
    
    // Normalize priorities within this aspiration
    this.normalizeAspirationPriorities(data.aspirationId);
    
    console.log(`✅ Created goal "${newGoal.title}" with priority ${newGoal.priority} in aspiration ${data.aspirationId}`);
    this.saveGoals();
    
    return newGoal;
  }

  // Update goal
  async updateGoal(id: string, updates: Partial<Goal>): Promise<Goal> {
    const goalIndex = this.goals.findIndex(goal => goal.id === id);
    if (goalIndex === -1) {
      throw new Error('Goal not found');
    }

    const updatedGoal = {
      ...this.goals[goalIndex],
      ...updates,
      updatedAt: new Date(),
    };

    // Handle priority changes within aspiration scope
    if (updates.priority && updates.priority !== this.goals[goalIndex].priority) {
      this.adjustAspirationPriorities(updates.priority, id, updatedGoal.aspirationId);
    }

    // Check if all lists are completed
    this.checkAutoCompletion(updatedGoal);

    // Auto-update status based on progress
    if (updates.progress !== undefined && updates.progress > 0 && updatedGoal.status === 'not_started') {
      updatedGoal.status = 'in_progress';
      updatedGoal.startDate = updatedGoal.startDate || new Date();
    }

    this.goals[goalIndex] = updatedGoal;
    this.saveGoals();
    
    return updatedGoal;
  }

  // Delete goal
  async deleteGoal(id: string): Promise<void> {
    const goalIndex = this.goals.findIndex(goal => goal.id === id);
    if (goalIndex === -1) {
      throw new Error('Goal not found');
    }

    this.goals.splice(goalIndex, 1);
    this.saveGoals();
  }

  // Reorder goals (within aspiration scope)
  async reorderGoals(reorderedGoals: Goal[]): Promise<void> {
    console.log('🔧 Goals Service: Starting reorderGoals within aspiration scope');
    console.log('📝 Goals Service: Received reordered goals:', 
      reorderedGoals.map(g => ({ id: g.id, title: g.title, priority: g.priority, aspirationId: g.aspirationId }))
    );
    
    if (reorderedGoals.length === 0) {
      console.log('🤷 Goals Service: No goals to reorder');
      return;
    }
    
    // Get the aspiration ID (all goals should have the same aspiration when reordering)
    const aspirationId = reorderedGoals[0].aspirationId;
    
    // Update priorities for all goals in the reordered list
    reorderedGoals.forEach((reorderedGoal, index) => {
      const existingGoal = this.goals.find(g => g.id === reorderedGoal.id);
      if (existingGoal) {
        const newPriority = index + 1; // Sequential priorities within aspiration
        console.log(`🎯 Goals Service: Updating "${existingGoal.title}" priority: ${existingGoal.priority} → ${newPriority}`);
        existingGoal.priority = newPriority;
        existingGoal.updatedAt = new Date();
      }
    });
    
    // Normalize priorities within the aspiration to ensure no gaps
    this.normalizeAspirationPriorities(aspirationId);
    
    console.log('✨ Goals Service: Final goals after aspiration-scoped reorder:', 
      this.goals
        .filter(g => g.aspirationId === aspirationId)
        .sort((a, b) => a.priority - b.priority)
        .map(g => ({ id: g.id, title: g.title, priority: g.priority }))
    );
    
    this.saveGoals();
  }

  // Add list to goal
  async addList(goalId: string, list: Omit<GoalList, 'id'>): Promise<GoalList> {
    const goal = this.goals.find(g => g.id === goalId);
    if (!goal) {
      throw new Error('Goal not found');
    }

    const newList: GoalList = {
      ...list,
      id: this.generateId(),
    };

    goal.lists.push(newList);
    goal.updatedAt = new Date();
    this.saveGoals();

    return newList;
  }

  // Update list
  async updateList(goalId: string, listId: string, updates: Partial<GoalList>): Promise<GoalList> {
    const goal = this.goals.find(g => g.id === goalId);
    if (!goal) {
      throw new Error('Goal not found');
    }

    const listIndex = goal.lists.findIndex(l => l.id === listId);
    if (listIndex === -1) {
      throw new Error('List not found');
    }

    const updatedList = {
      ...goal.lists[listIndex],
      ...updates,
    };

    if (updates.completed && !goal.lists[listIndex].completed) {
      updatedList.completedAt = new Date();
    }

    goal.lists[listIndex] = updatedList;
    goal.updatedAt = new Date();

    // Update goal progress based on list completion
    this.updateGoalProgressFromLists(goal);
    
    // Check if all lists are completed to auto-complete goal
    this.checkAutoCompletion(goal);
    
    this.saveGoals();

    return updatedList;
  }

  // Validate SMART criteria
  validateSmartCriteria(criteria: SmartCriteria): string[] {
    const errors: string[] = [];

    if (!criteria.specific.trim()) {
      errors.push('Specific: Please describe what exactly you will accomplish.');
    }

    if (!criteria.measurable.trim()) {
      errors.push('Measurable: Please describe how you will measure progress.');
    }

    if (!criteria.achievable.trim()) {
      errors.push('Achievable: Please describe how you will accomplish this goal.');
    }

    if (!criteria.relevant.trim()) {
      errors.push('Relevant: Please explain why this goal matters.');
    }

    if (!criteria.timeBound.trim()) {
      errors.push('Time-bound: Please specify when you will complete this goal.');
    }

    return errors;
  }

  // Private helper methods
  private generateId(): string {
    return 'goal_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Helper method to ensure all priorities are unique and sequential
  private normalizeAllPriorities(): void {
    console.log('🔧 Goals Service: Normalizing all priorities to ensure uniqueness');
    
    // Group goals by aspiration
    const goalsByAspiration = this.goals.reduce((acc, goal) => {
      if (!acc[goal.aspirationId]) {
        acc[goal.aspirationId] = [];
      }
      acc[goal.aspirationId].push(goal);
      return acc;
    }, {} as Record<string, Goal[]>);

    // Normalize priorities within each aspiration group
    Object.entries(goalsByAspiration).forEach(([aspirationId, goals]) => {
      this.normalizeAspirationPriorities(aspirationId, goals);
    });

    console.log('✅ Goals Service: All priorities normalized by aspiration:', 
      this.goals.map(g => ({ id: g.id, title: g.title, priority: g.priority, aspirationId: g.aspirationId }))
    );
  }

  // Helper method to normalize priorities within a specific aspiration
  private normalizeAspirationPriorities(aspirationId: string, providedGoals?: Goal[]): void {
    console.log(`🔧 Goals Service: Normalizing priorities for aspiration ${aspirationId}`);
    
    // Use provided goals or filter from all goals
    const aspirationGoals = providedGoals || this.goals.filter(g => g.aspirationId === aspirationId);
    
    // Sort by current priority, then by creation date as tiebreaker
    const sorted = [...aspirationGoals].sort((a, b) => {
      if (a.priority === b.priority) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return a.priority - b.priority;
    });

    // Reassign sequential priorities within the aspiration
    sorted.forEach((goal, index) => {
      const newPriority = index + 1;
      const existingGoal = this.goals.find(g => g.id === goal.id);
      if (existingGoal) {
        existingGoal.priority = newPriority;
        existingGoal.updatedAt = new Date();
      }
    });

    console.log(`✅ Goals Service: Priorities normalized for aspiration ${aspirationId}:`, 
      sorted.map(g => ({ id: g.id, title: g.title, priority: g.priority }))
    );
  }


  private adjustAspirationPriorities(newPriority: number, excludeId: string, aspirationId: string): void {
    console.log(`🔄 Goals Service: Adjusting priorities in aspiration ${aspirationId} for priority ${newPriority}`);
    
    this.goals.forEach(goal => {
      if (goal.id !== excludeId && 
          goal.aspirationId === aspirationId && 
          goal.priority >= newPriority) {
        const oldPriority = goal.priority;
        goal.priority += 1;
        goal.updatedAt = new Date();
        console.log(`  📈 Adjusted "${goal.title}": ${oldPriority} → ${goal.priority}`);
      }
    });
  }

  private updateGoalProgressFromLists(goal: Goal): void {
    if (goal.lists.length === 0) return;

    const completedLists = goal.lists.filter(l => l.completed).length;
    const progressFromLists = (completedLists / goal.lists.length) * 100;
    
    // Always update progress based on list completion
    goal.progress = Math.round(progressFromLists);
  }

  private checkAutoCompletion(goal: Goal): void {
    if (goal.lists.length === 0) return;

    const allListsCompleted = goal.lists.every(l => l.completed);

    if (allListsCompleted && goal.status !== 'completed') {
      goal.status = 'completed';
      goal.progress = 100;
      goal.completedDate = new Date();
      console.log(`🎉 Goal "${goal.title}" automatically marked as complete!`);
    } else if (!allListsCompleted && goal.status === 'completed') {
      // If a list is uncompleted, revert the goal status
      goal.status = 'in_progress';
      goal.completedDate = undefined;
    }
  }
}

// Export singleton instance
export const goalsService = new GoalsService();